import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Briefcase, CurrencyInr, TrendUp, TrendDown, ChartPie, Gear,
  Plus, ClipboardText, Truck, FileText, Warning, MagnifyingGlass,
  ChartLine, CheckCircle, XCircle, ArrowUpRight, ChartBar,
} from "@phosphor-icons/react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend, LineChart, Line, XAxis, YAxis, CartesianGrid } from "recharts";
import api, { formatApiError } from "@/lib/api";
import { formatCurrency, formatDate, amountInWords } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { isAdmin, isOwner } from "@/lib/permissions";
import { JobStatusSelect } from "@/components/JobStatusSelect";
import BudgetVsActualChart from "@/components/BudgetVsActualChart";
import BudgetOverrunAlert from "@/components/BudgetOverrunAlert";
import CostTrendChart from "@/components/CostTrendChart";
import WorkApprovals from "@/components/WorkApprovals";
import TeamChangeApprovals from "@/components/TeamChangeApprovals";
import EntityPage from "@/components/EntityPage";
import AllocationReminder from "@/components/AllocationReminder";
import WorkSessionsAdmin from "@/components/WorkSessionsAdmin";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip as UiTooltip, TooltipTrigger, TooltipContent, TooltipProvider,
} from "@/components/ui/tooltip";

const PIE_COLORS = ["#2563eb", "#16a34a", "#eab308", "#dc2626", "#7c3aed", "#0891b2", "#ea580c", "#db2777", "#65a30d", "#4f46e5", "#64748b"];

// variance: <=0 good(green), 0-10 caution(yellow), >10 problem(red)
const varianceClass = (pct) => (pct <= 0 ? "bg-emerald-100 text-emerald-700 border-emerald-200" : pct <= 10 ? "bg-amber-100 text-amber-700 border-amber-200" : "bg-red-100 text-red-700 border-red-200");
// utilization: >80 green, 50-80 yellow, <50 red
const utilClass = (pct) => (pct > 80 ? "bg-emerald-100 text-emerald-700 border-emerald-200" : pct >= 50 ? "bg-amber-100 text-amber-700 border-amber-200" : "bg-red-100 text-red-700 border-red-200");
const fullAmt = (v) => {
  const words = amountInWords(v);
  return formatCurrency(v) + (words ? ` (${words})` : "");
};
const STATUS_COLORS = {
  Active: "bg-emerald-100 text-emerald-700 border-emerald-200",
  Completed: "bg-blue-100 text-blue-700 border-blue-200",
  Delivered: "bg-violet-100 text-violet-700 border-violet-200",
  Draft: "bg-slate-100 text-slate-600 border-slate-200",
  Pending: "bg-amber-100 text-amber-700 border-amber-200",
  "In Progress": "bg-blue-100 text-blue-700 border-blue-200",
};

function Metric({ icon: Icon, label, value, sub, accent, testId, to, onClick, full }) {
  const navigate = useNavigate();
  const clickable = !!to;
  const fullText = full || String(value);
  return (
    <TooltipProvider delayDuration={80}>
    <Card
      className={`p-5 border-slate-200 shadow-none ${clickable ? "cursor-pointer transition-all hover:border-blue-300 hover:shadow-md hover:-translate-y-0.5 group" : ""}`}
      data-testid={testId}
      onClick={clickable ? () => (onClick ? onClick() : navigate(to)) : undefined}
      role={clickable ? "button" : undefined}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
            {label}
            {clickable && <ArrowUpRight className="h-3 w-3 opacity-0 group-hover:opacity-100 text-blue-600 transition-opacity" weight="bold" />}
          </div>
          <UiTooltip>
            <TooltipTrigger asChild>
              <div className="text-2xl font-bold text-slate-900 mt-1.5 truncate cursor-help" data-testid={testId ? `${testId}-value` : undefined}>{value}</div>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-[260px] text-center">
              <div className="text-[10px] uppercase tracking-wide opacity-70">{label}</div>
              <div className="text-base font-bold leading-tight" data-testid={testId ? `${testId}-tooltip` : undefined}>{fullText}</div>
            </TooltipContent>
          </UiTooltip>
          {sub && <div className="text-xs mt-1">{sub}</div>}
        </div>
        <div className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 ${accent}`}>
          <Icon className="h-5 w-5" weight="bold" />
        </div>
      </div>
    </Card>
    </TooltipProvider>
  );
}

function SectionCard({ title, icon: Icon, action, children }) {
  return (
    <Card className="border-slate-200 shadow-none">
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="h-5 w-5 text-slate-500" />}
          <h3 className="font-semibold text-slate-900">{title}</h3>
        </div>
        {action}
      </div>
      <div className="p-2 sm:p-4">{children}</div>
    </Card>
  );
}

function MachineTable({ rows, onTrend }) {
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader className="bg-slate-50">
          <TableRow>
            <TableHead>Machine ID</TableHead><TableHead>Name</TableHead><TableHead>Type</TableHead>
            <TableHead className="text-right">Planned Hrs</TableHead><TableHead className="text-right">Actual Hrs</TableHead>
            <TableHead className="text-center">Utilization</TableHead><TableHead className="text-right">Idle Hrs</TableHead>
            <TableHead className="text-center">Trend</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((m) => (
            <TableRow key={m.id} data-testid={`util-row-${m.code}`}>
              <TableCell className="font-mono font-medium">{m.code}</TableCell>
              <TableCell>{m.machine_name}</TableCell>
              <TableCell className="text-slate-500">{m.machine_type}</TableCell>
              <TableCell className="text-right">{m.planned_hours}</TableCell>
              <TableCell className="text-right">{m.actual_hours}</TableCell>
              <TableCell className="text-center"><Badge variant="outline" className={utilClass(m.utilization)}>{m.utilization}%</Badge></TableCell>
              <TableCell className="text-right">{m.idle_hours}</TableCell>
              <TableCell className="text-center">
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onTrend(m)} data-testid={`util-trend-${m.code}`} title="7-day trend">
                  <ChartLine className="h-4 w-4 text-blue-600" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function MachineTrendDialog({ machine, onClose }) {
  const { data } = useQuery({
    queryKey: ["util-trend", machine?.id],
    queryFn: async () => (await api.get(`/utilization/trend/${machine.id}`)).data.trend,
    enabled: !!machine,
  });
  return (
    <Dialog open={!!machine} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{machine ? `${machine.code} · 7-Day Utilization Trend` : ""}</DialogTitle>
        </DialogHeader>
        {data ? (
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="#94a3b8" />
              <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} stroke="#94a3b8" />
              <Tooltip formatter={(v) => `${v}%`} />
              <Line type="monotone" dataKey="utilization" name="Utilization %" stroke="#2563eb" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-[260px] flex items-center justify-center text-slate-400">Loading...</div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const admin = isAdmin(user?.role);
  const owner = isOwner(user?.role);
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => (await api.get("/dashboard/overview")).data,
  });

  const [statusFilter, setStatusFilter] = useState("All");
  const [clientSearch, setClientSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [opSort, setOpSort] = useState("efficiency");
  const [trendMachine, setTrendMachine] = useState(null);

  const setApproval = async (id, status) => {
    try {
      await api.put(`/production/${id}/approval`, { status });
      toast.success(`Log ${status}`);
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["alerts"] });
      qc.invalidateQueries({ queryKey: ["entity"] });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const filteredJobs = useMemo(() => {
    let jobs = data?.jobs_overview || [];
    if (statusFilter !== "All") jobs = jobs.filter((j) => j.job_status === statusFilter);
    if (clientSearch.trim()) jobs = jobs.filter((j) => (j.client_name || "").toLowerCase().includes(clientSearch.toLowerCase()));
    if (fromDate) jobs = jobs.filter((j) => j.start_date && j.start_date >= fromDate);
    if (toDate) jobs = jobs.filter((j) => j.start_date && j.start_date <= toDate);
    return jobs;
  }, [data, statusFilter, clientSearch, fromDate, toDate]);

  const sortedOps = useMemo(() => {
    const ops = [...(data?.operator_output || [])];
    const key = { efficiency: "output_pct", output: "units_produced", rework: "rework_scrap" }[opSort];
    return ops.sort((a, b) => b[key] - a[key]);
  }, [data, opSort]);

  if (isLoading || !data) return <div className="text-slate-400 py-20 text-center">Loading dashboard...</div>;

  const m = data.metrics;
  const varValue = m.budget_variance_value;
  const varPct = m.budget_variance_pct;

  const quickActions = [
    { label: "Start New Job", icon: Plus, to: "/forms/new-job", admin: true, testId: "qa-new-job" },
    { label: "Log Daily Production", icon: ClipboardText, to: "/forms/log-production", admin: false, testId: "qa-log-production" },
    { label: "Add Cost Entry", icon: CurrencyInr, to: "/forms/add-cost", admin: true, testId: "qa-add-cost" },
    { label: "Add External Process", icon: Truck, to: "/forms/add-external", admin: true, testId: "qa-add-external" },
    { label: "View Full Reports", icon: FileText, to: "/reports", admin: true, testId: "qa-reports" },
    { label: "Audit Log", icon: FileText, to: "/audit", owner: true, testId: "qa-audit" },
  ].filter((a) => (a.owner ? owner : admin));

  return (
    <div className="space-y-6">
      {/* Header + quick actions */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Workshop Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">Live overview of jobs, machines, output and costs</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {quickActions.map((a) => (
            <Button key={a.label} onClick={() => navigate(a.to)} variant={a.label === "Start New Job" ? "default" : "outline"} className={a.label === "Start New Job" ? "bg-blue-600 hover:bg-blue-700" : ""} data-testid={a.testId}>
              <a.icon className="h-4 w-4 mr-1.5" weight="bold" /> {a.label}
            </Button>
          ))}
        </div>
      </div>

      {/* 1. Key metric cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Metric icon={Briefcase} label="Active Jobs" value={m.active_jobs} full={`${m.active_jobs} active job${m.active_jobs === 1 ? "" : "s"}`} accent="bg-blue-100 text-blue-600" testId="metric-active-jobs" to="/jobs?q=Active" />
        <Metric icon={CurrencyInr} label="Budget Allocated" value={formatCurrency(m.total_budget_allocated)} full={fullAmt(m.total_budget_allocated)} sub={<span className="text-slate-400">active jobs</span>} accent="bg-violet-100 text-violet-600" testId="metric-budget-allocated" to="/jobs" />
        <Metric icon={TrendUp} label="Actual Cost So Far" value={formatCurrency(m.total_actual_cost)} full={fullAmt(m.total_actual_cost)} sub={<span className="text-slate-400">active jobs</span>} accent="bg-amber-100 text-amber-600" testId="metric-actual-cost" to="/costs" />
        <Metric icon={varValue > 0 ? TrendUp : TrendDown} label="Budget Variance" value={formatCurrency(Math.abs(varValue))}
          full={`${fullAmt(Math.abs(varValue))} · ${varPct > 0 ? "+" : ""}${varPct}% ${varPct <= 0 ? "under budget" : "over budget"}`}
          sub={<span className={varPct <= 0 ? "text-emerald-600 font-medium" : varPct <= 10 ? "text-amber-600 font-medium" : "text-red-600 font-medium"}>{varPct > 0 ? "+" : ""}{varPct}% {varPct <= 0 ? "under" : "over"}</span>}
          accent={varPct <= 0 ? "bg-emerald-100 text-emerald-600" : varPct <= 10 ? "bg-amber-100 text-amber-600" : "bg-red-100 text-red-600"} testId="metric-variance" to="/reports" />
        <Metric icon={ChartPie} label="Overall Profitability" value={`${m.overall_profitability}%`}
          full={`${m.overall_profitability}% ${m.overall_profitability >= 0 ? "profit" : "loss"} overall`}
          sub={<span className={m.overall_profitability >= 0 ? "text-emerald-600 font-medium" : "text-red-600 font-medium"}>{m.overall_profitability >= 0 ? "profit" : "loss"}</span>}
          accent="bg-emerald-100 text-emerald-600" testId="metric-profitability" to="/reports" />
      </div>

      {/* Budget overrun alerts */}
      <BudgetOverrunAlert jobs={data.jobs_overview} />

      {admin && <WorkApprovals />}
      {admin && <AllocationReminder />}
      {admin && <TeamChangeApprovals canDecide={isOwner(user?.role)} />}

      {admin && (
        <Card className="p-5 border-slate-200 shadow-none" data-testid="dashboard-production-log">
          <EntityPage entity="production" embedded />
        </Card>
      )}
      {admin && <WorkSessionsAdmin />}

      {/* Budget vs Actual chart */}
      <SectionCard title="Budget Allocated vs Actual Cost" icon={ChartBar}>
        <BudgetVsActualChart jobs={data.jobs_overview} />
      </SectionCard>

      {/* Cost trend */}
      <SectionCard title="Cost Trend — Spend vs Budget Pace" icon={ChartLine}>
        <CostTrendChart jobs={data.jobs_overview} />
      </SectionCard>

      {/* Approval Queue (managers) */}
      {admin && data.pending_approvals.length > 0 && (
        <SectionCard title={`Logs Awaiting Approval (${data.pending_approvals.length})`} icon={ClipboardText}>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader className="bg-slate-50">
                <TableRow>
                  <TableHead>Log ID</TableHead><TableHead>Date</TableHead><TableHead>Operator</TableHead>
                  <TableHead>Machine</TableHead><TableHead>Job</TableHead>
                  <TableHead className="text-right">Hrs</TableHead><TableHead className="text-right">Output</TableHead>
                  <TableHead className="text-center">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.pending_approvals.map((p) => (
                  <TableRow key={p.id} data-testid={`approval-row-${p.code}`}>
                    <TableCell className="font-mono text-xs">{p.code}</TableCell>
                    <TableCell>{formatDate(p.date)}</TableCell>
                    <TableCell>{p.operator}</TableCell>
                    <TableCell className="font-mono text-xs">{p.machine}</TableCell>
                    <TableCell className="font-mono text-xs">{p.job}</TableCell>
                    <TableCell className="text-right">{p.actual_hours}</TableCell>
                    <TableCell className="text-right">{p.actual_output}</TableCell>
                    <TableCell className="text-center">
                      <div className="flex gap-1 justify-center">
                        <Button size="sm" variant="outline" className="h-7 text-emerald-700 border-emerald-200 hover:bg-emerald-50" onClick={() => setApproval(p.id, "Approved")} data-testid={`approve-${p.code}`}>
                          <CheckCircle className="h-4 w-4 mr-1" weight="fill" /> Approve
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 text-red-700 border-red-200 hover:bg-red-50" onClick={() => setApproval(p.id, "Rejected")} data-testid={`reject-${p.code}`}>
                          <XCircle className="h-4 w-4 mr-1" weight="fill" /> Reject
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </SectionCard>
      )}

      {/* 2. Jobs overview */}
      <SectionCard title="Jobs Overview" icon={Briefcase}>
        <div className="flex flex-col sm:flex-row flex-wrap gap-3 mb-4 px-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-40" data-testid="jobs-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["All", "Draft", "Active", "Completed", "Delivered"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="relative flex-1 min-w-[180px]">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input placeholder="Search client..." value={clientSearch} onChange={(e) => setClientSearch(e.target.value)} className="pl-9" data-testid="jobs-client-search" />
          </div>
          <div className="flex items-center gap-2">
            <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="w-40" data-testid="jobs-from-date" />
            <span className="text-slate-400 text-sm">to</span>
            <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="w-40" data-testid="jobs-to-date" />
          </div>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader className="bg-slate-50">
              <TableRow>
                <TableHead>Job ID</TableHead><TableHead>Client</TableHead><TableHead>Job Name</TableHead>
                <TableHead>Type</TableHead><TableHead>Status</TableHead>
                <TableHead className="text-right">Allocated</TableHead><TableHead className="text-right">Spent</TableHead>
                <TableHead className="text-center">Variance %</TableHead><TableHead className="text-center">Profit %</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredJobs.length === 0 ? (
                <TableRow><TableCell colSpan={9} className="text-center py-8 text-slate-400">No jobs match the filters.</TableCell></TableRow>
              ) : filteredJobs.map((j) => (
                <TableRow key={j.id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/jobs/${j.id}`)} data-testid={`jobs-overview-row-${j.code}`}>
                  <TableCell className="font-mono font-medium text-blue-600">{j.code}</TableCell>
                  <TableCell>{j.client_name}</TableCell>
                  <TableCell className="max-w-[160px] truncate">{j.job_name}</TableCell>
                  <TableCell className="text-slate-500">{j.product_type}</TableCell>
                  <TableCell onClick={(e) => admin && e.stopPropagation()}>
                    {admin ? (
                      <JobStatusSelect jobId={j.id} status={j.job_status} code={j.code} invalidateKeys={[["dashboard"], ["entity", "jobs"]]} />
                    ) : (
                      j.job_status && <Badge variant="outline" className={STATUS_COLORS[j.job_status] || ""}>{j.job_status}</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">{formatCurrency(j.budget_allocated)}</TableCell>
                  <TableCell className="text-right">{formatCurrency(j.budget_spent)}</TableCell>
                  <TableCell className="text-center"><Badge variant="outline" className={varianceClass(j.variance_pct)}>{j.variance_pct > 0 ? "+" : ""}{j.variance_pct}%</Badge></TableCell>
                  <TableCell className="text-center font-semibold text-slate-700">{j.profitability}%</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </SectionCard>

      {/* 3. Machine utilization */}
      <SectionCard title="Machine Utilization — Today" icon={Gear}>
        <div className="px-2 mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Production Machines</div>
        <MachineTable rows={data.production_machines} onTrend={setTrendMachine} />
        <div className="px-2 mt-6 mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">QC / Inspection Equipment</div>
        <MachineTable rows={data.qc_machines} onTrend={setTrendMachine} />
      </SectionCard>

      {/* 4 & 5 side by side on large */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* 4. Operator output today */}
        <SectionCard title="Operator Output — Today" icon={ClipboardText}
          action={
            <Select value={opSort} onValueChange={setOpSort}>
              <SelectTrigger className="w-40 h-8" data-testid="operator-sort"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="efficiency">By Efficiency</SelectItem>
                <SelectItem value="output">By Output</SelectItem>
                <SelectItem value="rework">By Rework</SelectItem>
              </SelectContent>
            </Select>
          }>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader className="bg-slate-50">
                <TableRow>
                  <TableHead>Operator</TableHead><TableHead>Role</TableHead><TableHead>Machine</TableHead><TableHead>Job</TableHead>
                  <TableHead className="text-right">Units</TableHead><TableHead className="text-right">Hrs</TableHead>
                  <TableHead className="text-center">Output %</TableHead><TableHead className="text-right">Rework</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedOps.length === 0 ? (
                  <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-400">No production logged today.</TableCell></TableRow>
                ) : sortedOps.map((o, i) => (
                  <TableRow key={i} data-testid={`operator-row-${i}`}>
                    <TableCell className="font-medium">{o.operator_name}</TableCell>
                    <TableCell className="text-slate-500 text-xs">{o.role}</TableCell>
                    <TableCell className="font-mono text-xs">{o.machine}</TableCell>
                    <TableCell className="font-mono text-xs">{o.job}</TableCell>
                    <TableCell className="text-right">{o.units_produced}</TableCell>
                    <TableCell className="text-right">{o.hours_worked}</TableCell>
                    <TableCell className="text-center"><Badge variant="outline" className={utilClass(o.output_pct)}>{o.output_pct}%</Badge></TableCell>
                    <TableCell className="text-right">{o.rework_scrap > 0 ? <span className="text-red-600 font-medium">{o.rework_scrap}</span> : o.rework_scrap}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </SectionCard>

        {/* 5. Cost breakdown */}
        <SectionCard title="Cost Breakdown by Category" icon={ChartPie}>
          {data.cost_breakdown.length === 0 ? (
            <div className="h-[300px] flex items-center justify-center text-sm text-slate-400">No cost entries yet.</div>
          ) : (
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <ResponsiveContainer width="100%" height={260} className="max-w-[280px]">
                <PieChart>
                  <Pie data={data.cost_breakdown} dataKey="value" nameKey="category" cx="50%" cy="50%" outerRadius={95} innerRadius={45}>
                    {data.cost_breakdown.map((e, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => formatCurrency(v)} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex-1 w-full space-y-1.5">
                {data.cost_breakdown.map((c, i) => (
                  <div key={c.category} className="flex items-center justify-between text-sm" data-testid={`cost-cat-${i}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="h-3 w-3 rounded-sm shrink-0" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                      <span className="truncate text-slate-600">{c.category}</span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="font-medium text-slate-900">{formatCurrency(c.value)}</span>
                      <span className="text-xs text-slate-400 w-10 text-right">{c.pct}%</span>
                    </div>
                  </div>
                ))}
                <div className="flex items-center justify-between text-sm pt-2 mt-2 border-t border-slate-100 font-semibold">
                  <span>Total</span><span>{formatCurrency(data.total_cost)}</span>
                </div>
              </div>
            </div>
          )}
        </SectionCard>
      </div>

      {/* 6. External processes tracker */}
      <SectionCard title="External Processes Tracker" icon={Truck}>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader className="bg-slate-50">
              <TableRow>
                <TableHead>Job ID</TableHead><TableHead>Process</TableHead><TableHead>Vendor</TableHead>
                <TableHead>Status</TableHead><TableHead>Date Sent</TableHead><TableHead>Date Received</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.external_processes.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">No external processes.</TableCell></TableRow>
              ) : data.external_processes.map((e, i) => (
                <TableRow key={i} className={e.overdue ? "bg-red-50" : ""} data-testid={`ext-row-${i}`}>
                  <TableCell className="font-mono text-xs">{e.job}</TableCell>
                  <TableCell>{e.process_name}</TableCell>
                  <TableCell>{e.vendor_name}</TableCell>
                  <TableCell>
                    {e.overdue ? (
                      <Badge variant="outline" className="bg-red-100 text-red-700 border-red-200 gap-1"><Warning className="h-3 w-3" weight="fill" /> Overdue</Badge>
                    ) : (
                      <Badge variant="outline" className={STATUS_COLORS[e.status] || ""}>{e.status}</Badge>
                    )}
                  </TableCell>
                  <TableCell>{formatDate(e.date_sent)}</TableCell>
                  <TableCell>{e.date_received ? formatDate(e.date_received) : <span className="text-slate-400">Pending</span>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </SectionCard>

      <MachineTrendDialog machine={trendMachine} onClose={() => setTrendMachine(null)} />
    </div>
  );
}
